import { createElement, FC } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { UseSelectStateChange } from 'downshift';
import cn from 'classnames';
import { useMediaQuery } from 'usehooks-ts';
import { faSortAmountAsc } from '@fortawesome/free-solid-svg-icons';
import { createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import { Dropdown, DropdownItem, SortByDropdown } from '@app/components';
import { getPageStartNumber, wrapBoldTranslationChunks } from '@app/utils';
import {
  breakpoint,
  ShapeWithClassName,
  SortDirection,
  SortOption,
} from '@app/constants';

import './ResultWithSortAndPagination.scss';

interface ResultCountWithSortingProps {
  recordsCount: number;
  recordsPerPage: number;
  pageNumber: number;
  countMessage: MessageDescriptor;
  sortOptions: SortOption[];
  sortDirection: SortDirection;
  sortField?: string;
  titleLevel?: 1 | 2;
  onRecordsPerPageChange: ({
    selectedItem,
  }: UseSelectStateChange<DropdownItem>) => void;
  onSortChange: (option: SortOption) => void;
}

const cnBem = createBemBlockBuilder(['resultWithSortAndPagination']);

export const ResultWithSortAndPagination: FC<
  ShapeWithClassName<ResultCountWithSortingProps>
> = ({
  recordsCount,
  recordsPerPage,
  pageNumber,
  countMessage,
  sortOptions,
  sortDirection,
  sortField,
  className,
  titleLevel = 2,
  onRecordsPerPageChange,
  onSortChange,
}) => {
  const { $t, formatNumber } = useIntl();
  const isMobile = useMediaQuery(
    `(max-width: ${breakpoint.SCREEN.TABLET_LG - 1}px)`
  );

  const titlesPerPageDropdownItems = [25, 50, 100].map(value => ({
    label: $t(
      {
        id: 'searchResults.show',
        defaultMessage: 'Show: {value}',
      },
      { value: formatNumber(value) }
    ),
    mobileLabel: formatNumber(value),
    value,
  }));
  const titlesPerPageDropdownSelectedItem =
    titlesPerPageDropdownItems.find(item => item.value === recordsPerPage) ??
    titlesPerPageDropdownItems[0];
  const startNumber = getPageStartNumber({ recordsPerPage, pageNumber });
  const endNumber =
    recordsPerPage * pageNumber > recordsCount
      ? recordsCount
      : recordsPerPage * pageNumber;

  return (
    <div className={cn(cnBem('__container'), className)}>
      {createElement(
        `h${titleLevel}`,
        {
          className: cnBem('__count'),
          'data-auto': 'pagination-results-range',
        },
        $t(countMessage, {
          startNumber: formatNumber(startNumber),
          endNumber: formatNumber(endNumber),
          totalRecords: formatNumber(recordsCount),
          b: wrapBoldTranslationChunks,
        })
      )}
      <div className={cnBem('__controlsWrapper')}>
        <Dropdown
          hideLabel
          label={titlesPerPageDropdownSelectedItem.label}
          items={titlesPerPageDropdownItems}
          id="items-per-page-dropdown"
          selectedItem={titlesPerPageDropdownSelectedItem}
          styleType="standard"
          className={cn('compactDropdown', cnBem('__dropdownItem'))}
          onSelectedItemChange={onRecordsPerPageChange}
        />
        <SortByDropdown
          hideLabel
          options={sortOptions}
          sortField={sortField}
          sortDirection={sortDirection}
          toggleIcon={faSortAmountAsc}
          toggleIconOnly={isMobile}
          styleType="standard"
          className={cn('compactDropdown', cnBem('__dropdownItem'))}
          onSelect={onSortChange}
        />
      </div>
    </div>
  );
};
